import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { trpc } from '@/lib/trpc';
import { formatCurrency, formatDate } from '@/lib/utils';
import { downloadXlsx, dateStamp } from '@/lib/download-xlsx';
import { showErrorToast } from '@/hooks/use-mutation-with-toast';
import { useSessionState } from '@/hooks/use-session-state';
import { ReportExportButton } from './report-export-button';

type App = 'TRAVEL' | 'TRADING';

const entryHref = (app: App, id: string) =>
  app === 'TRAVEL'
    ? `/travel/accounting/journal-entries/${id}`
    : `/trading/accounting/journal-entries/${id}`;

/**
 * Defter-i Kebir: every account in the code range with its devir, each
 * movement with a running balance, period totals and closing balance.
 */
export function LedgerBookReport({ app, cashOnly = false }: { app: App; cashOnly?: boolean }) {
  const key = app.toLowerCase() + (cashOnly ? ':cash' : '');
  const today = new Date();
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [startDate, setStartDate] = useSessionState(
    `${key}:accounting-reports:ledger:startDate`,
    startOfMonth.toISOString().split('T')[0] ?? ''
  );
  const [endDate, setEndDate] = useSessionState(
    `${key}:accounting-reports:ledger:endDate`,
    today.toISOString().split('T')[0] ?? ''
  );
  const [codeFrom, setCodeFrom] = useSessionState(`${key}:accounting-reports:ledger:codeFrom`, '');
  const [codeTo, setCodeTo] = useSessionState(`${key}:accounting-reports:ledger:codeTo`, '');
  const [includeEmpty, setIncludeEmpty] = useSessionState(
    `${key}:accounting-reports:ledger:includeEmpty`,
    false
  );
  const [daily, setDaily] = useSessionState(`${key}:accounting-reports:ledger:daily`, false);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [exporting, setExporting] = useState(false);

  const input = {
    startDate: new Date(startDate || startOfMonth),
    endDate: cashOnly && daily && startDate
      ? new Date(`${startDate}T23:59:59.999Z`)
      : new Date(endDate || today),
    accountCodeFrom: cashOnly ? '100' : codeFrom || undefined,
    accountCodeTo: cashOnly ? '100' : codeTo || undefined,
    includeEmpty,
  };
  const travel = trpc.financialReports.getLedgerBook.useQuery(input, { enabled: app === 'TRAVEL' });
  const trading = trpc.tradingFinancialReports.getLedgerBook.useQuery(input, {
    enabled: app === 'TRADING',
  });
  const query = app === 'TRAVEL' ? travel : trading;
  const data = query.data;
  const utils = trpc.useUtils();

  const exportBook = async () => {
    setExporting(true);
    try {
      const res =
        app === 'TRAVEL'
          ? await utils.financialReports.exportLedgerBook.fetch(input)
          : await utils.tradingFinancialReports.exportLedgerBook.fetch(input);
      downloadXlsx(
        res.rows,
        cashOnly ? 'Kasa' : 'Kebir',
        `${cashOnly ? 'kasa' : 'kebir'}-${startDate}-${cashOnly && daily ? startDate : endDate}-${dateStamp()}.xlsx`
      );
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : String(e));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-card flex flex-wrap items-end gap-4 rounded-lg border p-4 print:hidden">
        <div className="space-y-2">
          <Label>{cashOnly && daily ? 'Rapor tarihi' : 'Başlangıç / From'}</Label>
          <Input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-44"
          />
        </div>
        <div className="space-y-2">
          <Label>Bitiş / To</Label>
          <Input
            disabled={cashOnly && daily}
            type="date"
            value={cashOnly && daily ? startDate : endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-44"
          />
        </div>
        <div className="space-y-2">
          <Label>Hesap kodu (başlangıç)</Label>
          <Input
            disabled={cashOnly}
            value={cashOnly ? '100' : codeFrom}
            onChange={(e) => setCodeFrom(e.target.value)}
            placeholder="100"
            className="w-32 font-mono"
          />
        </div>
        <div className="space-y-2">
          <Label>Hesap kodu (bitiş)</Label>
          <Input
            disabled={cashOnly}
            value={cashOnly ? '100' : codeTo}
            onChange={(e) => setCodeTo(e.target.value)}
            placeholder="399"
            className="w-32 font-mono"
          />
        </div>
        {cashOnly && (
          <label className="flex h-10 items-center gap-2 text-sm">
            <input type="checkbox" checked={daily} onChange={(e) => setDaily(e.target.checked)} />
            Günlük rapor
          </label>
        )}
        <label className="flex h-10 items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={includeEmpty}
            onChange={(e) => setIncludeEmpty(e.target.checked)}
          />
          Hareketsiz hesapları da göster
        </label>
      </div>

      {query.isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
        </div>
      ) : query.error ? (
        <div className="border-destructive/50 bg-destructive/10 text-destructive border p-4 text-sm">
          {query.error.message}
        </div>
      ) : data ? (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-muted-foreground text-sm">
              {data.totals.accounts} hesap · {data.totals.lines} hareket · Borç{' '}
              {formatCurrency(data.totals.periodDebit)} · Alacak{' '}
              {formatCurrency(data.totals.periodCredit)}
            </p>
            <ReportExportButton
              onExport={exportBook}
              busy={exporting}
              disabled={data.accounts.length === 0}
            />
          </div>

          {data.accounts.length === 0 ? (
            <div className="bg-card text-muted-foreground rounded-lg border p-6 text-sm">
              Bu dönemde hareket yok. / No movements in this period.
            </div>
          ) : (
            <div className="space-y-2">
              {data.accounts.map((account) => {
                const expanded = open[account.id] ?? data.accounts.length <= 8;
                return (
                  <div key={account.id} className="bg-card rounded-lg border">
                    <button
                      type="button"
                      className="hover:bg-muted/40 flex w-full flex-wrap items-center gap-3 px-4 py-2 text-left text-sm"
                      onClick={() => setOpen((o) => ({ ...o, [account.id]: !expanded }))}
                    >
                      {expanded ? (
                        <ChevronDown className="h-4 w-4 shrink-0" />
                      ) : (
                        <ChevronRight className="h-4 w-4 shrink-0" />
                      )}
                      <span className="font-mono text-xs">{account.code}</span>
                      <span className="font-medium">{account.name}</span>
                      <span className="text-muted-foreground ml-auto flex flex-wrap gap-4 text-xs">
                        <span>Devir {formatCurrency(account.openingBalance)}</span>
                        <span>Borç {formatCurrency(account.periodDebit)}</span>
                        <span>Alacak {formatCurrency(account.periodCredit)}</span>
                        <span className="text-foreground font-medium">
                          Bakiye {formatCurrency(account.closingBalance)}
                        </span>
                      </span>
                    </button>
                    {expanded && (
                      <div className="overflow-x-auto border-t">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-muted-foreground text-left text-[11px] uppercase tracking-wide">
                              <th className="px-4 py-1.5 font-medium">Tarih</th>
                              <th className="px-4 py-1.5 font-medium">Fiş</th>
                              <th className="px-4 py-1.5 font-medium">Açıklama</th>
                              <th className="px-4 py-1.5 text-right font-medium">Borç</th>
                              <th className="px-4 py-1.5 text-right font-medium">Alacak</th>
                              <th className="px-4 py-1.5 text-right font-medium">Bakiye</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="bg-muted/20 border-t text-xs">
                              <td className="px-4 py-1" colSpan={5}>
                                DEVİR
                              </td>
                              <td className="px-4 py-1 text-right font-mono">
                                {formatCurrency(account.openingBalance)}
                              </td>
                            </tr>
                            {account.lines.map((l, i) => (
                              <tr key={`${l.entryId}-${i}`} className="border-t">
                                <td className="whitespace-nowrap px-4 py-1">
                                  {formatDate(l.date)}
                                </td>
                                <td className="px-4 py-1">
                                  <Link
                                    to={entryHref(app, l.entryId)}
                                    className="font-mono text-xs hover:underline"
                                  >
                                    {l.entryNumber}
                                  </Link>
                                </td>
                                <td className="px-4 py-1">{l.description}</td>
                                <td className="px-4 py-1 text-right font-mono">
                                  {l.debit > 0 ? formatCurrency(l.debit) : ''}
                                </td>
                                <td className="px-4 py-1 text-right font-mono">
                                  {l.credit > 0 ? formatCurrency(l.credit) : ''}
                                </td>
                                <td className="px-4 py-1 text-right font-mono">
                                  {formatCurrency(l.balance)}
                                </td>
                              </tr>
                            ))}
                            <tr className="bg-muted/30 border-t text-xs font-semibold">
                              <td className="px-4 py-1" colSpan={3}>
                                DÖNEM TOPLAMI / KAPANIŞ
                              </td>
                              <td className="px-4 py-1 text-right font-mono">
                                {formatCurrency(account.periodDebit)}
                              </td>
                              <td className="px-4 py-1 text-right font-mono">
                                {formatCurrency(account.periodCredit)}
                              </td>
                              <td className="px-4 py-1 text-right font-mono">
                                {formatCurrency(account.closingBalance)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
